import React, { useState, useRef, useEffect } from 'react';
import './VideoTextPage.css';
import './VideoTextPageDID.css';

import { useStrings } from './strings/StringsContext';

// import ElevenLabsIntegration from './ElevenLabsIntegration';
// import AvatarWithLipSync from './AvatarWithLipSync';

// API Keys - Use environment variables in production
const ELEVENLABS_API_KEY =
  process.env.REACT_APP_ELEVENLABS_API_KEY ||
  'sk_bf6e7eac4526d7dd9b7419a691ff0a01f2e4cfeccf9d36c0';

// D-ID API Key
const DID_API_KEY = 'ZHJlYW1zb3dqYW55YUBnbWFpbC5jb20:3T3htz3eI-skUYKyARBCV'; // Use this for local
// const DID_API_KEY =
//   'amFuYXJkaGFuYm9iYmExNjU2QGdtYWlsLmNvbQ:PtiUELC1kfjI1-xVg8KZx';
// D-ID Presenter Source Image URL (must be a public URL)
const DID_SOURCE_URL =
  process.env.REACT_APP_DID_SOURCE_URL || 'https://i.imgur.com/8Km9tLL.jpg';

// Type declarations for Speech Recognition API
declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}

interface SpeechRecognitionEvent extends Event {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message: string;
}

interface VideoTextResult {
  text: string;
  confidence: number;
  timestamp: number;
}

const VideoTextPageDID: React.FC = () => {
    const { strings } = useStrings(); // ✅ Hook used correctly inside component

  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [extractedText, setExtractedText] = useState<VideoTextResult[]>([]);
  const [currentText, setCurrentText] = useState('');
  const [lastFinalText, setLastFinalText] = useState('');

  // Auto-processing state
  const [
    speechPauseTimer,
    setSpeechPauseTimer,
  ] = useState<NodeJS.Timeout | null>(null);
  const [lastSpeechTime, setLastSpeechTime] = useState<number>(0);
  const [lastProcessedText, setLastProcessedText] = useState<string>('');

  // Conversation state
  const [conversationHistory, setConversationHistory] = useState<
    Array<{ role: 'user' | 'assistant'; content: string }>
  >([]);
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null);
  const [audioStream, setAudioStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<string>('');
  const [settings, setSettings] = useState({
    language: 'en-US',
    continuous: true,
    interimResults: true,
    maxAlternatives: 1,
  });
  const [audioLevel, setAudioLevel] = useState(0);
  const [isListening, setIsListening] = useState(false);
  const [isSpeechPaused, setIsSpeechPaused] = useState(false);
  const [isRestarting, setIsRestarting] = useState(false);
  const [mediaRecorderSupported, setMediaRecorderSupported] = useState(true);
  const [microphoneTest, setMicrophoneTest] = useState<{
    isTesting: boolean;
    level: number;
    status: string;
  }>({
    isTesting: false,
    level: 0,
    status: 'Ready to test',
  });
  const [activeTab, setActiveTab] = useState<'extraction' | 'elevenlabs'>(
    'extraction'
  );
  const [audioWaveform, setAudioWaveform] = useState<number[]>([]);
  const [isMonitoringAudio, setIsMonitoringAudio] = useState(false);

  // ElevenLabs Integration State
  const [elevenLabsSettings, setElevenLabsSettings] = useState({
    apiKey: ELEVENLABS_API_KEY,
    selectedVoice: '21m00Tcm4TlvDq8ikWAM',
    stability: 0.5,
    similarityBoost: 0.75,
    style: 0.0,
    useSpeakerBoost: true,
  });
  const [isSynthesizing, setIsSynthesizing] = useState(false);
  const [synthesisStatus, setSynthesisStatus] = useState<string>('');
  const [generatedAudioUrl, setGeneratedAudioUrl] = useState<string>('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [elevenLabsError, setElevenLabsError] = useState<string>('');

  // OpenAI Integration State
  const [openAiResponse, setOpenAiResponse] = useState<string>('');
  const [isProcessingOpenAI, setIsProcessingOpenAI] = useState(false);
  const [openAiError, setOpenAiError] = useState<string>('');
  const [openAiStatus, setOpenAiStatus] = useState<string>('');

  // D-ID Integration State
  const [didVideoUrl, setDidVideoUrl] = useState<string>('');
  const [isGeneratingDID, setIsGeneratingDID] = useState(false);
  const [didStatus, setDidStatus] = useState<string>('');
  const [didError, setDidError] = useState<string>('');

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const recognitionRef = useRef<any>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const elevenLabsAudioRef = useRef<HTMLAudioElement>(null);
  const didVideoRef = useRef<HTMLVideoElement>(null);


  /* Video Text Page - D-ID Integration with script */
useEffect(() => {
  const script = document.createElement('script');
  script.src = "https://agent.d-id.com/v2/index.js";
  script.type = "module";
  script.async = true;
  script.setAttribute("data-mode", "full");
  script.setAttribute("data-client-key", "Z29vZ2xlLW9hdXRoMnwxMTU1MDMzNzM2MTQxMjg3MzMzNDQ6SzdhQW1FNUJmZHZKeW9ydDJnRnZS");
  script.setAttribute("data-agent-id", "v2_agt_MGV0RGgS");
  script.setAttribute("data-name", "did-agent");
  script.setAttribute("data-monitor", "true");
  script.setAttribute("data-target-id", "did-agent-container");

  document.body.appendChild(script);

  return () => {
    document.body.removeChild(script); // cleanup on unmount
  };
}, []);





  useEffect(() => {
    // Initialize speech recognition
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();

      recognition.continuous = settings.continuous;
      recognition.interimResults = settings.interimResults;
      recognition.lang = settings.language;
      recognition.maxAlternatives = settings.maxAlternatives;

      recognition.onstart = () => {
        console.log('Speech recognition started');
        setIsProcessing(true);
        setIsListening(true);
      };

      recognition.onresult = (event: SpeechRecognitionEvent) => {
        let finalTranscript = '';
        let interimTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          const confidence = event.results[i][0].confidence;

          if (event.results[i].isFinal) {
            finalTranscript += transcript;
            const newResult: VideoTextResult = {
              text: transcript,
              confidence: confidence,
              timestamp: Date.now(),
            };
            setExtractedText((prev) => [...prev, newResult]);

            // Store the final text to keep it visible
            setLastFinalText(transcript.trim());

            // Update last speech time for pause detection
            setLastSpeechTime(Date.now());

            // Clear existing timer
            if (speechPauseTimer) {
              clearTimeout(speechPauseTimer);
            }

            // Set new timer for auto-processing after speech pause
            const timer = setTimeout(() => {
              console.log('🎤 Speech pause detected, auto-processing...');
              // Auto-trigger will be handled by the useEffect
            }, 5000); // 5 second pause detection - longer gap tolerance

            setSpeechPauseTimer(timer);
          } else {
            interimTranscript += transcript;
          }
        }

        // Keep current text visible until new speech starts
        // Only update if there's new interim text, otherwise keep the last final text
        if (interimTranscript) {
          setCurrentText(interimTranscript);
        }
      };

      recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
        console.error('Speech recognition error:', event.error);

        // Handle specific error types
        switch (event.error) {
          case 'no-speech':
            setError(
              'No speech detected. Please check your microphone and try speaking louder.'
            );
            break;
          case 'audio-capture':
            setError(
              'Microphone not accessible. Please check your microphone permissions.'
            );
            break;
          case 'not-allowed':
            setError(
              'Microphone access denied. Please allow microphone access and refresh the page.'
            );
            break;
          case 'network':
            setError('Network error. Please check your internet connection.');
            break;
          case 'service-not-allowed':
            setError(
              'Speech recognition service not allowed. Please check your browser settings.'
            );
            break;
          default:
            setError(`Speech recognition error: ${event.error}`);
        }

        setIsProcessing(false);
        setIsListening(false);
      };

      recognition.onend = () => {
        console.log('Speech recognition ended');
        setIsProcessing(false);
        setIsListening(false);

        // Auto-restart speech recognition if still recording
        if (isRecording) {
          console.log('🔄 Auto-restarting speech recognition after gap...');
          setIsRestarting(true);
          setTimeout(() => {
            if (recognitionRef.current && isRecording) {
              try {
                recognitionRef.current.start();
                setIsListening(true);
                setIsRestarting(false);
                console.log('✅ Speech recognition restarted successfully');
              } catch (error) {
                console.error(
                  '❌ Failed to restart speech recognition:',
                  error
                );
                setIsRestarting(false);
                // Try again after a longer delay
                setTimeout(() => {
                  if (recognitionRef.current && isRecording) {
                    try {
                      recognitionRef.current.start();
                      setIsListening(true);
                      console.log('✅ Speech recognition restarted on retry');
                    } catch (retryError) {
                      console.error('❌ Retry failed:', retryError);
                    }
                  }
                }, 3000);
              }
            }
          }, 500); // Reduced delay for faster restart
        }
      };

      recognitionRef.current = recognition;
    } else {
      setError('Speech recognition not supported in this browser');
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [settings]);

  // Health check for speech recognition - restart if inactive for too long
  useEffect(() => {
    if (!isRecording) return;

    const healthCheckInterval = setInterval(() => {
      if (
        isRecording &&
        recognitionRef.current &&
        !isListening &&
        !isRestarting
      ) {
        console.log(
          '🔄 Health check: Restarting inactive speech recognition...'
        );
        try {
          recognitionRef.current.start();
          setIsListening(true);
        } catch (error) {
          console.error('❌ Health check restart failed:', error);
        }
      }
    }, 30000); // Check every 30 seconds

    return () => clearInterval(healthCheckInterval);
  }, [isRecording, isListening, isRestarting]);

  // Load ElevenLabs settings from localStorage
  useEffect(() => {
    const savedSettings = localStorage.getItem('elevenlabs-settings');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setElevenLabsSettings((prev) => ({ ...prev, ...parsed }));
      } catch (error) {
        console.error('Error loading ElevenLabs settings:', error);
      }
    }
  }, []);

  // Cleanup speech pause timer on unmount
  useEffect(() => {
    return () => {
      if (speechPauseTimer) {
        clearTimeout(speechPauseTimer);
      }
    };
  }, [speechPauseTimer]);

  // Auto-trigger complete workflow when text extraction is finished
  useEffect(() => {
    console.log('🔍 Auto-trigger check:', {
      extractedTextLength: extractedText.length,
      isProcessingOpenAI,
      isSynthesizing,
      isRecording,
      hasOpenAiResponse: !!openAiResponse,
      lastProcessedText,
    });

    // Check if we have extracted text and we're not already processing
    if (extractedText.length > 0 && !isProcessingOpenAI && !isSynthesizing) {
      const latestText = extractedText[extractedText.length - 1]?.text || '';
      const combinedText = latestText.trim();

      // Only auto-trigger if we have meaningful text and it's different from last processed
      if (combinedText && combinedText !== lastProcessedText) {
        console.log(
          '🚀 Auto-triggering complete workflow with NEW text:',
          combinedText
        );
        setLastProcessedText(combinedText); // Mark as being processed
        // Small delay to ensure UI is ready
        setTimeout(() => {
          console.log('🚀 Starting complete workflow now...');
          runCompleteWorkflow();
        }, 1000);
      } else if (combinedText === lastProcessedText) {
        console.log('⏭️ Skipping - text already processed:', combinedText);
      } else if (!combinedText) {
        console.log('⏭️ No text to process');
      }
    }
  }, [extractedText, isProcessingOpenAI, isSynthesizing, lastProcessedText]);

  const startVideoCapture = async () => {
    try {
      setError('');

      // Request camera and microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          frameRate: { ideal: 30 },
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
        },
      });

      setVideoStream(stream);
      setAudioStream(stream);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play(); // Ensure video plays
      }

      // Start speech recognition immediately
      if (
        recognitionRef.current &&
        recognitionRef.current.state !== 'started'
      ) {
        try {
          recognitionRef.current.start();
          setIsRecording(true);
          setIsProcessing(true);
        } catch (error) {
          console.error('Error starting speech recognition:', error);
          // If recognition is already started, just set the states
          setIsRecording(true);
          setIsProcessing(true);
        }
      }

      // Set up media recorder for audio capture (optional)
      if (
        stream.getAudioTracks().length > 0 &&
        typeof MediaRecorder !== 'undefined'
      ) {
        // Wait for stream to be ready
        const audioTrack = stream.getAudioTracks()[0];
        if (audioTrack.readyState === 'live') {
          // Check for supported MIME types
          const supportedTypes = [
            'audio/webm;codecs=opus',
            'audio/webm',
            'audio/mp4',
            'audio/wav',
          ];

          let selectedType = '';
          for (const type of supportedTypes) {
            if (MediaRecorder.isTypeSupported(type)) {
              selectedType = type;
              break;
            }
          }

          if (!selectedType) {
            console.warn('No supported audio format found, using default');
            selectedType = 'audio/webm';
          }

          try {
            const mediaRecorder = new MediaRecorder(stream, {
              mimeType: selectedType,
            });
            mediaRecorderRef.current = mediaRecorder;

            mediaRecorder.ondataavailable = (event) => {
              if (event.data.size > 0) {
                chunksRef.current.push(event.data);
              }
            };

            mediaRecorder.onstop = () => {
              const audioBlob = new Blob(chunksRef.current, {
                type: selectedType,
              });
              chunksRef.current = [];
              console.log('Audio recorded:', audioBlob);
            };

            mediaRecorder.onerror = (event) => {
              console.error('MediaRecorder error:', event);
              setError('Error recording audio. Please try again.');
            };

            // Start recording after a small delay to ensure stream is ready
            setTimeout(() => {
              try {
                if (mediaRecorder.state === 'inactive') {
                  mediaRecorder.start(1000); // Collect data every second
                }
              } catch (startError) {
                console.error('Error starting MediaRecorder:', startError);
                setMediaRecorderSupported(false);
                setError(
                  'Audio recording failed, but speech recognition will still work.'
                );
              }
            }, 500); // Increased delay to ensure stream is fully ready
          } catch (error) {
            console.error('Error creating MediaRecorder:', error);
            setMediaRecorderSupported(false);
            setError(
              'MediaRecorder not supported. Audio recording disabled, but speech recognition will still work.'
            );
          }

          // Set up audio level monitoring
          try {
            const audioContext = new AudioContext();
            const analyser = audioContext.createAnalyser();
            const microphone = audioContext.createMediaStreamSource(stream);
            const dataArray = new Uint8Array(analyser.frequencyBinCount);

            microphone.connect(analyser);
            analyser.fftSize = 256;

            const monitorAudioLevel = () => {
              if (isRecording) {
                analyser.getByteFrequencyData(dataArray);
                const average =
                  dataArray.reduce((a, b) => a + b) / dataArray.length;
                setAudioLevel(average / 255);
                requestAnimationFrame(monitorAudioLevel);
              }
            };

            monitorAudioLevel();
          } catch (error) {
            console.error('Error setting up audio monitoring:', error);
            // Continue without audio level monitoring
          }
        } else {
          console.warn('Audio track not ready, skipping MediaRecorder setup');
          setMediaRecorderSupported(false);
        }
      } else {
        console.warn('MediaRecorder not available, audio recording disabled');
        setMediaRecorderSupported(false);
      }
    } catch (err) {
      console.error('Error accessing camera/microphone:', err);
      if (err instanceof DOMException) {
        if (err.name === 'NotAllowedError') {
          setError(
            'Camera and microphone access denied. Please allow access and try again.'
          );
        } else if (err.name === 'NotFoundError') {
          setError('No camera or microphone found. Please check your devices.');
        } else {
          setError(`Error accessing camera/microphone: ${err.message}`);
        }
      } else {
        setError(
          `Error accessing camera/microphone: ${
            err instanceof Error ? err.message : String(err)
          }`
        );
      }
    }
  };
// Auto-start D-ID Agent
const startDIDAgent = () => {
  try {
    window.postMessage(
      {
        type: "d-id/agent-start"
      },
      "*"
    );
    console.log("✅ D-ID Agent start signal sent");
  } catch (err) {
    console.warn("⚠️ Auto-start failed", err);
  }
};




  const stopVideoCapture = () => {
    // Stop media recorder
    if (mediaRecorderRef.current && mediaRecorderSupported) {
      try {
        if (mediaRecorderRef.current.state !== 'inactive') {
          mediaRecorderRef.current.stop();
        }
      } catch (error) {
        console.error('Error stopping MediaRecorder:', error);
      }
    }

    // Stop video stream
    if (videoStream) {
      videoStream.getTracks().forEach((track) => track.stop());
      setVideoStream(null);
      setAudioStream(null);
    }

    // Stop speech recognition
    if (recognitionRef.current) {
      try {
        if (recognitionRef.current.state === 'started') {
          recognitionRef.current.stop();
        }
        // Ensure it does not auto-restart
        recognitionRef.current.abort?.();
      } catch (error) {
        console.error('Error stopping speech recognition:', error);
      }
    }

    setIsRecording(false);
    setIsProcessing(false);
    setIsListening(false);
    setIsSpeechPaused(false);
    setIsRestarting(false);
  };

  // Reset function to clear all states
 const resetAll = () => {
  stopVideoCapture();
  
  // remove D-ID script and UI
  const existing = document.querySelector('script[src="https://agent.d-id.com/v2/index.js"]');
  if (existing) existing.remove();
  const container = document.getElementById("did-agent-container");
  if (container) container.innerHTML = "";

  // reload D-ID agent script
  setTimeout(() => {
    const script = document.createElement('script');
    script.src = "https://agent.d-id.com/v2/index.js";
    script.type = "module";
    script.async = true;
    script.setAttribute("data-mode", "full");
    script.setAttribute("data-client-key", "Z29vZ2xlLW9hdXRoMnwxMTU1MDMzNzM2MTQxMjg3MzMzNDQ6SzdhQW1FNUJmZHZKeW9ydDJnRnZS");
    script.setAttribute("data-agent-id", "v2_agt_MGV0RGgS");
    script.setAttribute("data-monitor", "true");
    script.setAttribute("data-target-id", "did-agent-container");
    document.body.appendChild(script);
  }, 500);

  // reset app states
  setError('');
  setCurrentText('');
  setLastFinalText('');
  setExtractedText([]);
  setAudioLevel(0);
  setDidVideoUrl('');
  setOpenAiResponse('');
  setConversationHistory([]);
};

  const captureFrame = () => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const video = videoRef.current;
      const ctx = canvas.getContext('2d');

      if (ctx) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0);

        // Convert canvas to blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              // Here you could send the frame for OCR processing
              console.log('Frame captured:', blob);
            }
          },
          'image/jpeg',
          0.8
        );
      }
    }
  };

  const clearResults = () => {
    setExtractedText([]);
    setCurrentText('');
    setLastFinalText('');
    setLastProcessedText(''); // Reset processed text tracking
  };

  const exportResults = () => {
    const results = extractedText.map((result) => ({
      text: result.text,
      confidence: result.confidence,
      timestamp: new Date(result.timestamp).toISOString(),
    }));

    const dataStr = JSON.stringify(results, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `video-text-extraction-${Date.now()}.json`;
    link.click();

    URL.revokeObjectURL(url);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      // You could add a toast notification here
      console.log('Text copied to clipboard');
    });
  };

  const testMicrophone = async () => {
    try {
      setMicrophoneTest((prev) => ({
        ...prev,
        isTesting: true,
        status: 'Testing microphone...',
      }));

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const audioContext = new AudioContext();
      const analyser = audioContext.createAnalyser();
      const microphone = audioContext.createMediaStreamSource(stream);
      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      microphone.connect(analyser);
      analyser.fftSize = 256;

      let testCount = 0;
      const testInterval = setInterval(() => {
        analyser.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
        const level = average / 255;

        setMicrophoneTest((prev) => ({
          ...prev,
          level: level,
          status: `Level: ${Math.round(level * 100)}% - ${
            level > 0.1 ? 'Microphone working!' : 'Speak louder...'
          }`,
        }));

        testCount++;
        if (testCount >= 30) {
          // Test for 3 seconds
          clearInterval(testInterval);
          stream.getTracks().forEach((track) => track.stop());
          audioContext.close();

          setMicrophoneTest((prev) => ({
            ...prev,
            isTesting: false,
            status:
              level > 0.1
                ? 'Microphone is working!'
                : 'Microphone detected but no audio. Try speaking louder.',
          }));
        }
      }, 100);
    } catch (error) {
      console.error('Microphone test error:', error);
      setMicrophoneTest((prev) => ({
        ...prev,
        isTesting: false,
        status: 'Microphone access denied or not available',
      }));
    }
  };

  const startAudioMonitoring = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const audioContext = new AudioContext();
      const analyser = audioContext.createAnalyser();
      const microphone = audioContext.createMediaStreamSource(stream);

      analyser.fftSize = 256;
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      microphone.connect(analyser);
      setIsMonitoringAudio(true);

      const updateWaveform = () => {
        if (isMonitoringAudio) {
          analyser.getByteFrequencyData(dataArray);

          // Convert frequency data to waveform
          const waveform = Array.from(dataArray).slice(0, 32); // Use first 32 bins
          setAudioWaveform(waveform);

          // Calculate average level
          const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
          setAudioLevel(average / 255);

          requestAnimationFrame(updateWaveform);
        }
      };

      updateWaveform();

      // Store references for cleanup
      (window as any).audioStream = stream;
      (window as any).audioContext = audioContext;
    } catch (error) {
      console.error('Error starting audio monitoring:', error);
      setError('Failed to access microphone for audio monitoring');
    }
  };

  const stopAudioMonitoring = () => {
    setIsMonitoringAudio(false);
    setAudioWaveform([]);
    setAudioLevel(0);

    if ((window as any).audioStream) {
      (window as any).audioStream
        .getTracks()
        .forEach((track: MediaStreamTrack) => track.stop());
    }
    if ((window as any).audioContext) {
      (window as any).audioContext.close();
    }
  };

  // ElevenLabs Text-to-Speech Synthesis
  const synthesizeWithElevenLabs = async (text: string) => {
    if (!text.trim()) {
      setElevenLabsError('No text to synthesize');
      return;
    }

    setIsSynthesizing(true);
    setElevenLabsError('');
    setSynthesisStatus('Synthesizing speech with ElevenLabs...');

    try {
      const response = await fetch(
        `https://api.elevenlabs.io/v1/text-to-speech/${elevenLabsSettings.selectedVoice}`,
        {
          method: 'POST',
          headers: {
            'xi-api-key': ELEVENLABS_API_KEY,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            text: text,
            model_id: 'eleven_monolingual_v1',
            voice_settings: {
              stability: elevenLabsSettings.stability,
              similarity_boost: elevenLabsSettings.similarityBoost,
              style: elevenLabsSettings.style,
              use_speaker_boost: elevenLabsSettings.useSpeakerBoost,
            },
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      setGeneratedAudioUrl(audioUrl);
      setSynthesisStatus('Speech synthesized successfully!');

      // Auto-play the audio
      if (elevenLabsAudioRef.current) {
        elevenLabsAudioRef.current.src = audioUrl;
        elevenLabsAudioRef.current.load();

        const playPromise = elevenLabsAudioRef.current.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              console.log('Audio started playing successfully');
              setIsPlaying(true);
            })
            .catch((error) => {
              console.log('Auto-play was prevented by browser:', error);
              setSynthesisStatus(
                '✅ Audio ready! Click the play button to hear the response.'
              );
            });
        } else {
          setIsPlaying(true);
        }
      }
    } catch (error) {
      console.error('Error synthesizing speech:', error);
      setElevenLabsError(
        'Failed to synthesize speech. Please check your API key and try again.'
      );
    } finally {
      setIsSynthesizing(false);
    }
  };

  // Process all extracted text with ElevenLabs
  const processAllExtractedText = async () => {
    if (extractedText.length === 0) {
      setElevenLabsError('No extracted text to process');
      return;
    }

    const combinedText = extractedText
      .map((result) => result.text)
      .join(' ')
      .trim();

    if (combinedText) {
      await synthesizeWithElevenLabs(combinedText);
    }
  };

  // Audio event handlers for ElevenLabs
  const handleElevenLabsPlayStart = () => {
    console.log('🎵 Audio started playing');
    setIsPlaying(true);

    // Pause speech recognition while AI audio is playing
    if (recognitionRef.current && isListening) {
      console.log('🎵 Pausing speech recognition during AI audio playback');
      setIsSpeechPaused(true);
      recognitionRef.current.stop();
    }
  };

  const handleElevenLabsPlayEnd = () => {
    console.log('🎵 Audio finished playing');
    setIsPlaying(false);

    // Restart speech recognition after AI audio finishes
    if (isRecording && recognitionRef.current && isSpeechPaused) {
      console.log('🎵 Restarting speech recognition after AI audio finished');
      setIsSpeechPaused(false);
      setTimeout(() => {
        if (recognitionRef.current && isRecording) {
          recognitionRef.current.start();
        }
      }, 1000); // Small delay to ensure audio is fully finished
    }

    // Clear the audio URL and response after audio finishes
    setTimeout(() => {
      setOpenAiResponse('');
      setOpenAiStatus('');
      setGeneratedAudioUrl('');
      console.log('🔄 Audio finished, ready for next sentence...');
    }, 1000); // Small delay to ensure audio is fully finished
  };

  const handleElevenLabsPlayError = (error: any) => {
    console.error('🎵 Audio playback error:', error);
    setIsPlaying(false);
  };

  const handleElevenLabsPlayPause = () => {
    console.log('🎵 Audio paused');
    setIsPlaying(false);
  };

  // D-ID video playback handlers to prevent feedback into speech recognition
  const handleDidPlayStart = () => {
    console.log('🎬 Video started playing');
    // Force-pause speech recognition while avatar audio is playing
    setIsSpeechPaused(true);
    setIsListening(false);
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        try {
          // Some browsers prefer abort when not started
          recognitionRef.current.abort?.();
        } catch (_) {
          // ignore
        }
      }
    }
  };

  const handleDidPlayEnd = () => {
    console.log('🎬 Video finished playing');
    // Restart speech recognition after avatar audio finishes
    if (isRecording && recognitionRef.current && isSpeechPaused) {
      setIsSpeechPaused(false);
      const attemptRestart = (delay: number) =>
        setTimeout(() => {
          if (!isRecording || !recognitionRef.current) return;
          try {
            recognitionRef.current.start();
            setIsListening(true);
            console.log('✅ Recognition restarted after video end');
          } catch (e) {
            console.warn('Retry failed to restart recognition:', e);
          }
        }, delay);

      // Multiple retries for robustness
      attemptRestart(800);
      attemptRestart(3000);
      attemptRestart(8000);
    }
  };

  const handleDidPlayPause = () => {
    // If video paused/stopped early, ensure recognition resumes soon
    if (isRecording && recognitionRef.current && isSpeechPaused) {
      setIsSpeechPaused(false);
      try {
        recognitionRef.current.start();
        setIsListening(true);
      } catch (e) {
        console.warn('Failed to restart recognition on video pause:', e);
      }
    }
  };

  // Watchdog: periodically ensure recognition is running when it should be
  useEffect(() => {
    const interval = setInterval(() => {
      if (
        isRecording &&
        recognitionRef.current &&
        !isListening &&
        !isRestarting &&
        !isSpeechPaused
      ) {
        try {
          recognitionRef.current.start();
          setIsListening(true);
          console.log('🔄 Watchdog restarted recognition');
        } catch (e) {
          // ignore
        }
      }
    }, 15000);
    return () => clearInterval(interval);
  }, [isRecording, isListening, isRestarting, isSpeechPaused]);

  // OpenAI Text Processing - Direct API Call
  const processWithOpenAI = async (text: string) => {
    if (!text.trim()) {
      setOpenAiError('No text to process');
      return;
    }

    setIsProcessingOpenAI(true);
    setOpenAiError('');
    setOpenAiStatus('Processing with OpenAI...');

    try {
      const response = await fetch(
        'https://api.openai.com/v1/chat/completions',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${
              process.env.REACT_APP_OPENAI_API_KEY ||
              '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'
            }`,
          },
          body: JSON.stringify({
            model: 'gpt-3.5-turbo',
            messages: [
              {
                role: 'system',
                content:
                  'You are a helpful AI assistant. Provide clear, concise, and helpful responses. Keep responses conversational and engaging.',
              },
              {
                role: 'user',
                content: `Please analyze and provide insights on the following text: ${text}`,
              },
            ],
            max_tokens: 500,
            temperature: 0.7,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          `OpenAI API error: ${errorData.error?.message || response.statusText}`
        );
      }

      const data = await response.json();
      const aiResponse =
        data.choices?.[0]?.message?.content || 'No response received';
      setOpenAiResponse(aiResponse);
      setOpenAiStatus('OpenAI processing completed successfully!');

      // Auto-trigger ElevenLabs conversion after OpenAI response
      console.log(
        '🤖 Auto-triggering ElevenLabs conversion for AI response...'
      );
      setTimeout(() => {
        synthesizeWithElevenLabs(aiResponse);
      }, 500); // Small delay to ensure state is updated
    } catch (error) {
      console.error('Error processing with OpenAI:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error occurred';
      setOpenAiError(`Failed to process text with OpenAI: ${errorMessage}`);
    } finally {
      setIsProcessingOpenAI(false);
    }
  };

  // Process all extracted text with OpenAI
  const processAllTextWithOpenAI = async () => {
    if (extractedText.length === 0) {
      setOpenAiError('No extracted text to process');
      return;
    }

    const combinedText = extractedText
      .map((result) => result.text)
      .join(' ')
      .trim();

    if (combinedText) {
      await processWithOpenAI(combinedText);
    }
  };

  // D-ID Video Generation (Text → D-ID handles TTS + Lip-sync)
  const generateDIDVideo = async (text: string) => {
    if (!text.trim()) {
      setDidError('No text to generate video for');
      return;
    }

    if (!DID_SOURCE_URL) {
      setDidError(
        'Missing presenter image. Please set REACT_APP_DID_SOURCE_URL to a public image URL.'
      );
      return;
    }

    setIsGeneratingDID(true);
    setDidError('');
    setDidStatus('Generating video...');

    try {
      // Create a D-ID CLIP with standard presenter (no source_url needed)
      const didResponse = await fetch('https://api.d-id.com/clips', {
        method: 'POST',
        headers: {
          Authorization: `Basic ${btoa(DID_API_KEY)}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          script: { type: 'text', input: text },
          presenter_id: 'v2_public_Amber@0zSz8kflCN',
        }),
      });

      if (!didResponse.ok) {
        const errorData = await didResponse.json();
        throw new Error(
          `API error: ${errorData.detail || didResponse.statusText}`
        );
      }

      const didData = await didResponse.json();
      const talkId = didData.id;

      // Poll for completion
      let attempts = 0;
      const maxAttempts = 120; // ~6 minutes at 3s interval

      const pollStatus = async () => {
        try {
          const statusResponse = await fetch(
            `https://api.d-id.com/clips/${talkId}`,
            {
              headers: {
                Authorization: `Basic ${btoa(DID_API_KEY)}`,
              },
            }
          );

          if (!statusResponse.ok) {
            throw new Error(`Status check failed: ${statusResponse.status}`);
          }

          const statusData = await statusResponse.json();

          // Update progress feedback
          setDidStatus(
            statusData.status === 'done'
              ? '✅ video generated successfully!'
              : `⏳ Generating video... (check ${attempts + 1}/${maxAttempts})`
          );

          if (statusData.status === 'done') {
            // Try to fetch the video and create a blob URL for reliable playback
            try {
              const videoResp = await fetch(statusData.result_url);
              if (!videoResp.ok) throw new Error('Video fetch failed');
              const videoBlob = await videoResp.blob();
              const localUrl = URL.createObjectURL(videoBlob);
              setDidVideoUrl(localUrl);
            } catch (e) {
              // Fallback to direct URL if blob fetch fails
              setDidVideoUrl(statusData.result_url);
            }
            setDidStatus('✅ video generated successfully!');
            setIsGeneratingDID(false);
          } else if (statusData.status === 'error') {
            throw new Error(`generation failed: ${statusData.error}`);
          } else {
            attempts++;
            if (attempts < maxAttempts) {
              setTimeout(pollStatus, 3000); // Check every 3 seconds for snappier updates
            } else {
              throw new Error('generation timed out');
            }
          }
        } catch (error) {
          console.error('Error polling status:', error);
          setDidError(
            `Failed to check status: ${
              error instanceof Error ? error.message : 'Unknown error'
            }`
          );
          setIsGeneratingDID(false);
        }
      };

      // Start polling after a short delay
      setTimeout(pollStatus, 2000);
    } catch (error) {
      console.error('Error generating video:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error occurred';
      setDidError(`Failed to generate video: ${errorMessage}`);
      setIsGeneratingDID(false);
    }
  };

  // Complete Workflow: Voice → Text → OpenAI → ElevenLabs → D-ID
  const runCompleteWorkflow = async () => {
    if (extractedText.length === 0) {
      setOpenAiError('No extracted text to process');
      return;
    }

    // Get only the latest sentence for processing (conversation context)
    const latestText = extractedText[extractedText.length - 1]?.text || '';
    const combinedText = latestText.trim();

    console.log('🎤 Processing latest sentence:', combinedText);

    if (!combinedText) {
      setOpenAiError('No text to process');
      return;
    }

    try {
      // Step 1: Process with OpenAI
      setOpenAiStatus(`🤖 Processing: "${combinedText}"`);
      setIsProcessingOpenAI(true);
      setOpenAiError('');

      const openAiResponse = await fetch(
        'https://api.openai.com/v1/chat/completions',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${
              process.env.REACT_APP_OPENAI_API_KEY ||
              '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'
            }`,
          },
          body: JSON.stringify({
            model: 'gpt-3.5-turbo',
            messages: [
              {
                role: 'system',
                content: `You are a helpful AI assistant with a voice that can speak to users. You have voice capabilities and can respond audibly. You can answer ANY question the user asks - whether it's about dates, general knowledge, help with tasks, explanations, advice, casual conversation, or anything else. 

                You are having a continuous conversation with the user. Respond naturally and conversationally as if you are speaking to someone. Keep responses brief, friendly, and direct. Answer the user's question directly without unnecessary explanations.

                Current date and time: ${new Date().toLocaleString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                  hour12: true,
                })}. 
                Week number: ${Math.ceil(
                  (new Date().getTime() -
                    new Date(new Date().getFullYear(), 0, 1).getTime()) /
                    (7 * 24 * 60 * 60 * 1000)
                )} of ${new Date().getFullYear()}.`,
              },
              // Include conversation history for context
              ...conversationHistory.slice(-6), // Keep last 6 exchanges for context
              {
                role: 'user',
                content: combinedText,
              },
            ],
            max_tokens: 500,
            temperature: 0.7,
          }),
        }
      );

      if (!openAiResponse.ok) {
        const errorData = await openAiResponse.json();
        throw new Error(
          `OpenAI API error: ${
            errorData.error?.message || openAiResponse.statusText
          }`
        );
      }

      const openAiData = await openAiResponse.json();
      const aiResponseText =
        openAiData.choices?.[0]?.message?.content || 'No response received';

      setOpenAiResponse(aiResponseText);
      setOpenAiStatus('✅ OpenAI processing completed!');
      setIsProcessingOpenAI(false);

      // Update conversation history
      setConversationHistory((prev) =>
        [
          ...prev,
          { role: 'user' as const, content: combinedText },
          { role: 'assistant' as const, content: aiResponseText },
        ].slice(-10)
      ); // Keep last 10 exchanges

      // Step 2: Generate D-ID video with lip sync
      console.log('🎬 Generating D-ID video with lip sync...');
      setDidStatus('🎬 Creating realistic avatar with lip sync...');
      await generateDIDVideo(aiResponseText);

      setOpenAiStatus(
        '🎉 Complete workflow finished! AI response converted to realistic avatar with lip sync.'
      );
    } catch (error) {
      console.error('Error in complete workflow:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error occurred';
      setOpenAiError(`Workflow failed: ${errorMessage}`);
      setDidError(`Workflow failed: ${errorMessage}`);
      setIsProcessingOpenAI(false);
      setIsGeneratingDID(false);
    }
  };

  return (
    <div className='video-text-page'>
      <div className='video-text-container'>
        <header className='video-text-header'>
<h1>{strings?.page_headers?.video_text_page_title || "SakCare Agent"} (Powered by Agentforce)</h1>
          {/* <p>
            Speak naturally and watch your AI assistant respond with realistic
            human-like avatar and lip sync
          </p> */}
        </header>

        <div className='video-text-content'>
          {/* Main Content Section - Video and Avatar in One Row */}
          <section className='main-content-section'>
            {/* Video and Avatar Row */}
            <div className='video-avatar-row'>
              {/* Video Preview Section */}
              <div className='video-section'>
                <div className='video-preview'>
                  <video
                    ref={videoRef}
                    autoPlay
                    muted
                    playsInline
                    className='video-element'
                  />
                  <canvas ref={canvasRef} style={{ display: 'none' }} />

                  {!isRecording && (
                    <div className='video-placeholder'>
                      <div className='placeholder-content'>
                        <div className='placeholder-icon'><img src="/DID video img.png" alt="Placeholder Icon" /></div>
                        <p>
                          Click "Turn on Video" to talk with your
                          AI agent
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Speech Recognition Status */}
                  {isRecording && (
                    <div className='speech-status'>
                      {isSpeechPaused ? (
                        <div className='speech-status-paused'>
                          🎵 Speech recognition paused during AI audio playback
                        </div>
                      ) : isRestarting ? (
                        <div className='speech-status-restarting'>
                          🔄 Restarting speech recognition after gap...
                        </div>
                      ) : (
                        <div className='speech-status-active'>
                          🎤 Speech recognition active - speak naturally
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Current Extracted Text Display */}
                {(currentText || lastFinalText) && (
                  <div className='current-text-display'>
                    <h4>🎤 Current Recognition:</h4>
                    <div className='current-text-content'>
                      <p
                        className={`interim-text ${
                          currentText ? 'live' : 'final'
                        }`}
                      >
                        {currentText || lastFinalText}
                      </p>
                    </div>
                  </div>
                )}

                <div className='video-controls'>
                  {!isRecording ? (
                    <button
                      className='btn btn-primary btn-large'
onClick={() => {
  startVideoCapture();
  setTimeout(() => startDIDAgent(), 1200);
}}                      disabled={isProcessing}
                    >
                      Turn on Video
                    </button>
                  ) : (
                    <button
                      className='btn btn-danger btn-large'
                      onClick={stopVideoCapture}
                    >
                      Turn off Video
                    </button>
                  )}

                  {/* <button
                    className='btn btn-secondary'
                    onClick={resetAll}
                    style={{ marginLeft: '0.5rem' }}
                  >
                    🔄 Reset All
                  </button> */}
                </div>
              </div>

              {/* D-ID Avatar Section */}
              <div className={`ai-avatar-section ${isPlaying ? 'playing' : ''}`}>
                <div id="did-agent-container"></div>
              </div>

            </div>
          </section>

          {/* Conversation History Section - Fixed Header with Scrollable Content */}
          {conversationHistory.length > 0 && (
            <div className='conversation-section'>
              <div className='conversation-header'>
                <h4>💬 Conversation History</h4>
                <span className='conversation-count'>
                  {conversationHistory.length} messages
                </span>
              </div>
              <div className='conversation-content'>
                {conversationHistory.map((message, index) => (
                  <div
                    key={index}
                    className={`conversation-message ${message.role}`}
                  >
                    <div className='message-header'>
                      <div className='message-role'>
                        {message.role === 'user' ? '👤 You' : '🤖 AI'}
                      </div>
                      <div className='message-timestamp'>
                        {new Date(
                          Date.now() -
                            (conversationHistory.length - index - 1) * 1000
                        ).toLocaleTimeString('en-US', {
                          hour12: false,
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                        })}
                      </div>
                    </div>
                    <div className='message-content'>{message.content}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Hidden Audio Element for ElevenLabs */}
          {generatedAudioUrl && (
            <audio
              ref={elevenLabsAudioRef}
              src={generatedAudioUrl}
              autoPlay
              onPlay={handleElevenLabsPlayStart}
              onEnded={handleElevenLabsPlayEnd}
              onPause={handleElevenLabsPlayPause}
              onError={handleElevenLabsPlayError}
              onLoadStart={() => console.log('🎵 Audio loading started')}
              onCanPlay={() => console.log('🎵 Audio can play')}
              onLoadedData={() => console.log('🎵 Audio data loaded')}
              style={{ display: 'none' }}
            />
          )}

          {/* Status Messages */}
          {synthesisStatus && (
            <div
              className='status-message success'
              style={{
                margin: '0.5rem 0',
                padding: '0.5rem',
                background: '#d4edda',
                color: '#155724',
                borderRadius: '4px',
              }}
            >
              {synthesisStatus}
            </div>
          )}
          {elevenLabsError && (
            <div
              className='status-message error'
              style={{
                margin: '0.5rem 0',
                padding: '0.5rem',
                background: '#f8d7da',
                color: '#721c24',
                borderRadius: '4px',
              }}
            >
              {elevenLabsError}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoTextPageDID;
